/**
 * API Client
 *
 * Fetch wrapper with error handling for Commander API
 */

import { ApiError, type ApiErrorResponse } from './types/errors';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

/**
 * Make an API request with automatic JSON handling and error parsing
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  // Handle no-content responses
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse response body
  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    if (isJson) {
      const errorBody = (await response.json()) as ApiErrorResponse;
      throw ApiError.fromResponse(response.status, errorBody);
    }
    throw new ApiError(response.status, await response.text());
  }

  if (isJson) {
    return response.json() as Promise<T>;
  }

  return response.text() as unknown as T;
}

/**
 * Build query string from filter object
 */
export function buildQueryString<T extends object>(params: T): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Upload a file via multipart/form-data
 */
export async function uploadFile<T>(
  path: string,
  file: File,
  fieldName = 'file'
): Promise<T> {
  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const errorBody = (await response.json()) as ApiErrorResponse;
      throw ApiError.fromResponse(response.status, errorBody);
    }
    throw new ApiError(response.status, await response.text());
  }

  return response.json() as Promise<T>;
}
